import React, { useState, useEffect } from 'react';
import { usePlatformAuth } from '@/context/PlatformAuthContext';
import PlatformLayout from '@/components/layout/PlatformLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Loader2, Briefcase, Building2, Search, Check, 
  AlertTriangle, IndianRupee, ArrowRight
} from 'lucide-react';
import { toast } from 'sonner';

const Enterprise = () => {
  const { fetchWithAuth } = usePlatformAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [activating, setActivating] = useState(false);
  const [formData, setFormData] = useState({
    monthly_price: 32000,
    transactions_per_month: 2500,
    notes: ''
  });

  useEffect(() => {
    loadCustomers();
  }, [search]);

  const loadCustomers = async () => {
    setLoading(true);
    try {
      let url = `/api/platform/customers?page=1&page_size=50`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      
      const response = await fetchWithAuth(url);
      if (!response.ok) throw new Error('Failed to load customers');
      
      const data = await response.json();
      setCustomers(data.customers || []);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async () => {
    if (!selectedOrg) {
      toast.error('Please select an organization');
      return;
    }

    setActivating(true);
    try {
      const response = await fetchWithAuth('/api/platform/enterprise/activate', {
        method: 'POST',
        body: JSON.stringify({
          organization_id: selectedOrg.organization_id,
          monthly_price: formData.monthly_price * 100, // Convert to paise
          transactions_per_month: formData.transactions_per_month,
          notes: formData.notes
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to activate enterprise');
      }

      toast.success('Enterprise subscription activated successfully!');
      setSelectedOrg(null);
      setFormData({
        monthly_price: 32000,
        transactions_per_month: 2500,
        notes: ''
      });
      loadCustomers();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setActivating(false);
    }
  };

  const tierColors = {
    DEMO: 'bg-purple-500/10 text-purple-400',
    STARTER: 'bg-blue-500/10 text-blue-400',
    GROWTH: 'bg-green-500/10 text-green-400',
    TRADER: 'bg-orange-500/10 text-orange-400',
    BUSINESS: 'bg-cyan-500/10 text-cyan-400',
    ENTERPRISE: 'bg-pink-500/10 text-pink-400'
  };

  return (
    <PlatformLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-white">Enterprise Activation</h1>
          <p className="text-slate-400 mt-1">Manually activate custom enterprise subscriptions</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Customer Selection */}
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white">Select Organization</CardTitle>
              <CardDescription className="text-slate-400">
                Choose the organization to upgrade to Enterprise
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search organizations..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-500"
                  data-testid="enterprise-search"
                />
              </div>

              {/* Customer List */}
              <div className="max-h-80 overflow-y-auto space-y-2">
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
                  </div>
                ) : customers.length === 0 ? (
                  <p className="text-center text-slate-400 py-8">No organizations found</p>
                ) : (
                  customers.map((customer) => (
                    <div
                      key={customer.organization_id}
                      onClick={() => setSelectedOrg(customer)}
                      className={`
                        p-4 rounded-lg cursor-pointer transition-colors border
                        ${selectedOrg?.organization_id === customer.organization_id
                          ? 'bg-blue-500/10 border-blue-500'
                          : 'bg-slate-700/50 border-transparent hover:bg-slate-700'
                        }
                      `}
                      data-testid={`select-org-${customer.organization_id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-slate-600 flex items-center justify-center">
                            <Building2 className="w-5 h-5 text-slate-300" />
                          </div>
                          <div>
                            <p className="font-medium text-white">{customer.name}</p>
                            <p className="text-sm text-slate-400">
                              {customer.subscription?.plan_name || 'No Plan'}
                            </p>
                          </div>
                        </div>
                        {customer.subscription && (
                          <Badge className={tierColors[customer.subscription.plan_tier] || 'bg-slate-600'}>
                            {customer.subscription.plan_tier}
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>

          {/* Activation Form */}
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white">Enterprise Configuration</CardTitle>
              <CardDescription className="text-slate-400">
                Set custom pricing and limits for this enterprise deal
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {selectedOrg ? (
                <>
                  {/* Selected Organization */}
                  <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/20">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-blue-400" />
                      </div>
                      <div>
                        <p className="font-medium text-white">{selectedOrg.name}</p>
                        <p className="text-sm text-blue-300">
                          Current: {selectedOrg.subscription?.plan_name || 'No Plan'}
                          {selectedOrg.subscription?.plan_tier === 'ENTERPRISE' && (
                            <span className="ml-2 text-pink-400">(Already Enterprise)</span>
                          )}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Warning if already enterprise */}
                  {selectedOrg.subscription?.plan_tier === 'ENTERPRISE' && (
                    <Alert className="bg-orange-500/10 border-orange-500/20">
                      <AlertTriangle className="h-4 w-4 text-orange-400" />
                      <AlertDescription className="text-orange-300">
                        This organization is already on Enterprise. Activating will reset their subscription.
                      </AlertDescription>
                    </Alert>
                  )}

                  {/* Pricing */}
                  <div className="space-y-2">
                    <Label className="text-slate-300">Monthly Price (₹)</Label>
                    <div className="relative">
                      <IndianRupee className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        type="number"
                        value={formData.monthly_price}
                        onChange={(e) => setFormData({ ...formData, monthly_price: parseInt(e.target.value) })}
                        className="pl-10 bg-slate-700/50 border-slate-600 text-white"
                        data-testid="enterprise-price"
                      />
                    </div>
                    <p className="text-xs text-slate-500">
                      + 18% GST = ₹{Math.round(formData.monthly_price * 1.18).toLocaleString()}
                    </p>
                  </div>

                  {/* Transactions */}
                  <div className="space-y-2">
                    <Label className="text-slate-300">Transactions per Month</Label>
                    <Input
                      type="number"
                      value={formData.transactions_per_month}
                      onChange={(e) => setFormData({ ...formData, transactions_per_month: parseInt(e.target.value) })}
                      className="bg-slate-700/50 border-slate-600 text-white"
                      data-testid="enterprise-transactions"
                    />
                    <p className="text-xs text-slate-500">
                      Rollover: {Math.floor(formData.transactions_per_month * 0.5)} (50% of monthly)
                    </p>
                  </div>

                  {/* Notes */}
                  <div className="space-y-2">
                    <Label className="text-slate-300">Internal Notes</Label>
                    <Textarea
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="Deal details, contact person, special terms..."
                      className="bg-slate-700/50 border-slate-600 text-white min-h-24 placeholder:text-slate-500"
                    />
                  </div>

                  {/* Summary */}
                  <div className="p-4 rounded-lg bg-slate-700/50 space-y-2">
                    <h4 className="font-medium text-white">Deal Summary</h4>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Monthly Revenue</span>
                      <span className="text-white">₹{formData.monthly_price.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Annual Revenue</span>
                      <span className="text-green-400">₹{(formData.monthly_price * 12).toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Cost per Transaction</span>
                      <span className="text-white">
                        ₹{(formData.monthly_price / formData.transactions_per_month).toFixed(2)}
                      </span>
                    </div>
                  </div>

                  {/* Activate Button */}
                  <Button
                    onClick={handleActivate}
                    disabled={activating}
                    className="w-full bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700"
                    data-testid="activate-enterprise"
                  >
                    {activating ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Activating...
                      </>
                    ) : (
                      <>
                        <Briefcase className="w-4 h-4 mr-2" />
                        Activate Enterprise
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </>
              ) : (
                <div className="text-center py-12">
                  <Building2 className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-400">Select an organization to configure</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </PlatformLayout>
  );
};

export default Enterprise;
